import { GlobalDataContext } from '@/contexts/GlobalDataContext';
import { GlobalUserDataContext } from '@/contexts/GlobalUserDataContext';
import { ScreenContext } from '@/contexts/ScreenContext';
import { TableBetContext } from '@/contexts/TableBetContext';
import { TableInfoContext } from '@/contexts/TableInfoContext';
import { useIsLobby } from '@/hooks/useIsLobby';
import { GameType } from '@/models/games/enums/GameType';
import { BetType as PokDengBetType } from '@/models/games/pokdeng/BetType';
import { Bet } from '@/models/host/BetAmount';
import { IcoTotalBet } from '@/modules/common/svg';
import { getLiveDataState } from '@/modules/main/slice/liveData';
import { RwdTypography } from '@/modules/rwdGame/components/RwdTypography';
import { HostProps, getVHByRem } from '@/utils/commonFunc';
import { Box, Stack, Typography } from '@mui/material';
import { ReactNode, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { FONT_SIZE } from './BalanceInfo';
import './TotalBetAmount.scss';

type TotalBetAmountProp = {
    iconSize: string;
    fontSize: string | { xs: string; md: string; lg: string };
};
export const TotalBetAmount = (props: HostProps) => {
    const { isLandscape } = useContext(ScreenContext);
    const [iconSize, setIconSize] = useState<string>('1.2rem');
    const [fontSize, setFontSize] = useState<
        string | { xs: string; md: string; lg: string }
    >(FONT_SIZE);
    const { isMultiBet } = useContext(GlobalDataContext);

    useEffect(() => {
        setIconSize(isLandscape ? getVHByRem(1.2) : '1.2rem');
        setFontSize(isLandscape ? getVHByRem(1.02) : FONT_SIZE);
    }, [isLandscape]);

    const [display, setDisplay] = useState<ReactNode>(<></>);

    useEffect(() => {
        switch (props.gameType) {
            case GameType.PokDeng:
                //Have withhold Game
                setDisplay(
                    <TotalBetAmountWithHold
                    //iconSize={iconSize}
                    //fontSize={fontSize}
                    />
                );
                break;
            case GameType.Blackjack:
                //No ABC Game
                setDisplay(<TotalBetAmountBJ />);
                break;
            default:
                if (isMultiBet) {
                    setDisplay(<TotalBetAmountMultiBet />);
                } else {
                    setDisplay(
                        <TotalBetAmountCommon
                            iconSize={iconSize}
                            fontSize={fontSize}
                        />
                    );
                }
        }
    }, [props.gameType, isMultiBet, iconSize, fontSize]);
    return display;
};

const TotalBetAmountBJ = () => {
    const { TableTotalBet, PendingTotalBet } = useContext(TableBetContext);
    const [amount, setAmount] = useState<string>('0.00');
    useEffect(() => {
        let amount = TableTotalBet - PendingTotalBet;
        setAmount(
            (amount / 100).toLocaleString('en-US', {
                maximumFractionDigits: 2,
                minimumFractionDigits: 2,
            })
        );
    }, [TableTotalBet, PendingTotalBet]);
    return (
        <Box className="total-bet-amount-in-game-container">
            <IcoTotalBet className="ico-total-bet" />
            <RwdTypography
                stepIndex={3}
                landscapeStepIndex={4}
                color={amount !== '0.00' ? '#F7D193' : '#FFFFFF'}
                fontWeight={amount !== '0.00' ? 'bold' : 'regular'}
            >
                {amount}
            </RwdTypography>
        </Box>
    );
};
const TotalBetAmountCommon = (prop: TotalBetAmountProp) => {
    const { iconSize, fontSize } = prop;
    const { TableTotalBet, PendingTotalBet } = useContext(TableBetContext);
    const { isAutoConfirm } = useContext(GlobalUserDataContext);
    const [amount, setAmount] = useState<string>('0.00');
    const isLobby = useIsLobby();
    useEffect(() => {
        let amount = TableTotalBet;
        if (isAutoConfirm === false) {
            amount -= PendingTotalBet;
        }
        setAmount(
            (amount / 100).toLocaleString('en-US', {
                maximumFractionDigits: 2,
                minimumFractionDigits: 2,
            })
        );
    }, [TableTotalBet, PendingTotalBet, isAutoConfirm]);
    return (
        <>
            {isLobby && (
                <Stack
                    direction={'row'}
                    display={'flex'}
                    alignItems={'center'}
                    height={iconSize}
                >
                    <Box width={iconSize} height={iconSize} display={'flex'}>
                        <IcoTotalBet />
                    </Box>
                    <Stack
                        direction={'row'}
                        className="my-auto"
                        spacing={0.5}
                        alignItems="flex-end"
                    >
                        <Typography
                            fontSize={fontSize}
                            lineHeight={1}
                            color={amount !== '0.00' ? '#F7D193' : '#FFFFFF'}
                            fontWeight={amount !== '0.00' ? 'bold' : 'regular'}
                        >
                            {amount}
                        </Typography>
                    </Stack>
                </Stack>
            )}
            {!isLobby && (
                <Box className="total-bet-amount-in-game-container">
                    <IcoTotalBet className="ico-total-bet" />
                    <RwdTypography
                        stepIndex={3}
                        landscapeStepIndex={4}
                        color={amount !== '0.00' ? '#F7D193' : '#FFFFFF'}
                        fontWeight={amount !== '0.00' ? 'bold' : 'regular'}
                    >
                        {amount}
                    </RwdTypography>
                </Box>
            )}
        </>
    );
};

const TotalBetAmountMultiBet = () => {
    const [amount, setAmount] = useState<string>('0.00');
    const { totalBaccaratBetAmount } = useSelector(getLiveDataState);
    useEffect(() => {
        setAmount(
            (totalBaccaratBetAmount && totalBaccaratBetAmount > 0
                ? totalBaccaratBetAmount / 100
                : 0
            ).toLocaleString('en-US', {
                maximumFractionDigits: 2,
                minimumFractionDigits: 2,
            })
        );
    }, [totalBaccaratBetAmount]);
    return (
        <Box className="total-bet-amount-in-game-container">
            <IcoTotalBet className="ico-total-bet" />
            <RwdTypography
                stepIndex={3}
                landscapeStepIndex={4}
                color={amount !== '0.00' ? '#F7D193' : '#FFFFFF'}
                fontWeight={amount !== '0.00' ? 'bold' : 'regular'}
            >
                {amount}
            </RwdTypography>
        </Box>
    );
};

const TotalBetAmountWithHold = () => {
    //prop: TotalBetAmountProp) => {
    //const { iconSize, fontSize } = prop;
    const { hostId } = useContext(TableInfoContext);
    const { isAutoConfirm } = useContext(GlobalUserDataContext);
    const { hostsBetInfo } = useSelector(getLiveDataState);
    const [amount, setAmount] = useState<string>('0.00');
    const [withHold, setWithHold] = useState<string>('0.00');
    const { t } = useTranslation();
    useEffect(() => {
        if (hostsBetInfo) {
            const item = hostsBetInfo.find(v => v.hostId === hostId);
            if (item) {
                let amount = item.totalBetAmount;
                let withHoldAmount = item.totalWithHoldAmount;
                if (!isAutoConfirm) {
                    amount -= item.pendingBetAmount;
                    withHoldAmount -= item.pendingWithHoldAmount;
                }
                setAmount(
                    (amount / 100).toLocaleString('en-US', {
                        maximumFractionDigits: 2,
                        minimumFractionDigits: 2,
                    })
                );
                setWithHold(
                    (withHoldAmount / 100).toLocaleString('en-US', {
                        maximumFractionDigits: 2,
                        minimumFractionDigits: 2,
                    })
                );
            } else {
                setWithHold('0.00');
                setAmount('0.00');
            }
        }
    }, [hostsBetInfo, hostId, isAutoConfirm]);
    return (
        <Box className="total-bet-amount-in-game-container">
            <IcoTotalBet className="ico-total-bet" />
            <RwdTypography
                stepIndex={3}
                landscapeStepIndex={4}
                color={amount !== '0.00' ? '#E2B875' : '#FFFFFF'}
                fontWeight={amount !== '0.00' ? 'bold' : 'regular'}
            >
                {amount}
                {' ('}
                {t('game.withHold')}
                {withHold}
                {')'}
            </RwdTypography>
        </Box>
    );
};

export const getBetWithHold = (gameType: GameType) => {
    let withHold = new Array<Bet>();
    switch (gameType) {
        case GameType.PokDeng:
            withHold.push({
                Type: PokDengBetType.PDBPlayer1Win,
                Amount: 1,
                GameId: 0,
            });
            withHold.push({
                Type: PokDengBetType.PDBPlayer2Win,
                Amount: 1,
                GameId: 0,
            });
            withHold.push({
                Type: PokDengBetType.PDBPlayer3Win,
                Amount: 1,
                GameId: 0,
            });
            withHold.push({
                Type: PokDengBetType.PDBPlayer4Win,
                Amount: 1,
                GameId: 0,
            });
            withHold.push({
                Type: PokDengBetType.PDBPlayer5Win,
                Amount: 1,
                GameId: 0,
            });
    }
    return withHold.length === 0 ? undefined : withHold;
};
